import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  getEquipmentTypesDisplay,
  getServiceTypeDisplayLabel,
  ISOStringToLocalReadableTimestamp,
} from "utils";

import { getRegionByCode } from "./utils";

const EXPIRY_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  year: "numeric",
};

export interface LaneSelectorItemProps {
  lane: ShipmentSearch;
  isSelected: boolean;
  onClick: VoidFunction;
  onDeleteClick: VoidFunction;
  onEditClick: VoidFunction;
}

export function LaneSelectorItem(props: LaneSelectorItemProps) {
  const { lane, isSelected, onClick, onDeleteClick, onEditClick } = props;
  const isExpired = lane.status === "EXPIRED";

  const equipmentDisplay = lane.equipment_types?.length
    ? getEquipmentTypesDisplay(lane.equipment_types)
    : "Any";
  const servicesDisplay = lane.services?.length
    ? lane.services.map((s: string) => getServiceTypeDisplayLabel(s)).join(", ")
    : "Any";
  const originsDisplay = lane.origin_provinces?.length
    ? lane.origin_provinces.map((p: string) => getRegionByCode(p)).join(", ")
    : "Any";
  const destinationsDisplay = lane.destination_provinces?.length
    ? lane.destination_provinces
        .map((p: string) => getRegionByCode(p))
        .join(", ")
    : "Any";

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: isSelected ? "action.selected" : "transparent",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Stack direction="row" alignItems="flex-start">
        <Box
          flex={1}
          onClick={onClick}
          sx={{
            cursor: "pointer",
            "&:hover": { opacity: 0.8 },
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="subtitle1"
              color={isExpired ? "text.secondary" : "primary"}
              fontWeight="medium"
            >
              {lane.name}
            </Typography>
            {isExpired && (
              <Tooltip title="This lane no longer matches new loads. Update its expiry to start matching again.">
                <Chip label="Expired" size="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={3} sx={{ mb: 0.25 }}>
            <Typography variant="body2">
              {"Equipment: "}
              <Typography component="span" variant="body2" color="primary">
                {equipmentDisplay}
              </Typography>
            </Typography>
            <Typography variant="body2">
              {"Services: "}
              <Typography component="span" variant="body2" color="primary">
                {servicesDisplay}
              </Typography>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3} sx={{ mb: 0.25 }}>
            <Typography variant="body2">
              {"Origins: "}
              <Typography component="span" variant="body2" color="primary">
                {originsDisplay}
              </Typography>
            </Typography>
            <Typography variant="body2">
              {"Destinations: "}
              <Typography component="span" variant="body2" color="primary">
                {destinationsDisplay}
              </Typography>
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {`Created: ${ISOStringToLocalReadableTimestamp(lane.created_at)}`}
            {lane.expires_at &&
              ` · ${isExpired ? "Expired" : "Expires"}: ${ISOStringToLocalReadableTimestamp(lane.expires_at, EXPIRY_DATE_FORMAT)}`}
          </Typography>
        </Box>
        <Tooltip title="Edit lane">
          <IconButton
            size="small"
            aria-label="Edit lane"
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete lane">
          <IconButton
            size="small"
            aria-label="Delete lane"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick();
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
